import React, { useEffect, useRef, useState } from "react";
import { parseExpression } from "../shared/pricing";

// How many ExpressionNumberInputs have focus right now (0 or 1 in practice).
// The undo watcher holds its burst open while this is non-zero, so typing a
// quantity is one undo step however slowly it is typed — the field now
// commits on every keystroke, which a 300ms debounce alone would split.
let numberFieldsEditing = 0;

/** True while a quantity field has focus — the undo watcher keeps its burst
 *  open until the edit ends. */
export function isNumberFieldEditing(): boolean {
  return numberFieldsEditing > 0;
}

export default function ExpressionNumberInput({
  value,
  onValueChange,
  placeholder,
  className,
  ariaLabel,
  readOnly,
  displayDecimals,
}: {
  value: number;
  onValueChange: (value: number) => void;
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
  readOnly?: boolean;
  /** Round the shown number while the field is NOT focused (display only —
   *  the stored value keeps full precision). Focusing shows the full value so
   *  a focus-then-blur can't commit the rounded text over it. */
  displayDecimals?: number;
}) {
  const full = (n: number) => (n === 0 ? "" : String(n));
  const shown = (n: number) =>
    n === 0 ? "" : displayDecimals == null ? String(n) : n.toFixed(displayDecimals);
  const [text, setText] = useState(shown(value));
  const isEditing = useRef(false);

  useEffect(() => {
    if (!isEditing.current) {
      setText(shown(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, displayDecimals]);

  // A field unmounted while focused (its card collapsed) never blurs.
  useEffect(
    () => () => {
      if (isEditing.current) {
        isEditing.current = false;
        numberFieldsEditing = Math.max(0, numberFieldsEditing - 1);
      }
    },
    []
  );

  // Commit as you type whenever the text is a complete number/expression.
  // Waiting for blur meant Ctrl+S with the cursor still in the field saved
  // the OLD value, and closing the window from there lost the typed number
  // with no warning. A partial expression ("12*") is held until it parses;
  // blur still runs the full commit below to tidy the text.
  const handleTextChange = (next: string) => {
    setText(next);
    const trimmed = next.trim();
    if (trimmed === "") {
      if (value !== 0) onValueChange(0);
      return;
    }
    const parsed = parseExpression(trimmed);
    if (parsed !== null && parsed !== value) onValueChange(parsed);
  };

  const commit = () => {
    const trimmed = text.trim();
    if (trimmed === "") {
      onValueChange(0);
      setText("");
      return;
    }
    const parsed = parseExpression(trimmed);
    if (parsed !== null) {
      onValueChange(parsed);
      setText(shown(parsed));
    } else {
      setText(shown(value));
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      pattern="[0-9+\-*/().=\s]*"
      className={className}
      placeholder={placeholder}
      value={text}
      readOnly={readOnly}
      aria-label={ariaLabel}
      onFocus={() => {
        if (!isEditing.current) numberFieldsEditing += 1;
        isEditing.current = true;
        if (displayDecimals != null) setText(full(value));
      }}
      onBlur={() => {
        if (isEditing.current) {
          numberFieldsEditing = Math.max(0, numberFieldsEditing - 1);
        }
        isEditing.current = false;
        commit();
      }}
      onChange={(e) => handleTextChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          (e.currentTarget as HTMLInputElement).blur();
        }
      }}
    />
  );
}
